import { createFileRoute, notFound } from "@tanstack/react-router";
import { i18nLinks, i18nOgMeta } from "@/lib/seo";
import { getLocale } from "@/paraglide/runtime";
import {
  blogPostsQueryOptions,
  blogToolsQueryOptions,
} from "@/tools/blog/hooks/use-blog-data";
import { pageDeps, pageSearch } from "@/tools/blog/lib/page-search";
import { BlogIndex } from "@/tools/blog/pages/BlogIndex";

export const Route = createFileRoute("/blog/")({
  component: BlogIndexRoute,
  ssr: true,
  // page 1 carries no ?page param so the canonical /blog URL stays clean.
  // The same sanitizer runs in loaderDeps — see page-search.ts for why.
  validateSearch: pageSearch,
  loaderDeps: pageDeps,
  loader: async ({ context, deps }) => {
    const locale = getLocale();
    // Await tools (fast, small dataset)
    await context.queryClient.ensureQueryData(blogToolsQueryOptions(locale));
    // Await the requested page to prevent SSR/client hydration mismatch.
    // featured = true: page 1 fetches an extra post so the grid stays full.
    const postsPage = await context.queryClient.ensureQueryData(
      blogPostsQueryOptions(locale, undefined, undefined, deps.page, true),
    );
    // Past the last page → real 404, not a 200 with an empty grid (soft 404).
    if (deps.page > 1 && postsPage.items.length === 0) throw notFound();
  },
  head: () => ({
    meta: [
      { title: "Toolverse Blog — F1 Fantasy Strategy, Guides & Data" },
      {
        name: "description",
        content:
          "In-depth F1 Fantasy strategy, driver and constructor analysis, race guides, and data-driven insights to help you win your mini-league every weekend.",
      },
      {
        property: "og:title",
        content: "Toolverse Blog — F1 Fantasy Strategy & Guides",
      },
      ...i18nOgMeta("/blog"),
    ],
    links: [...i18nLinks("/blog")],
  }),
});

function BlogIndexRoute() {
  const { page } = Route.useSearch();
  return <BlogIndex page={page ?? 1} />;
}
